import { router } from '@inertiajs/core';
import { usePage } from '@inertiajs/react';
import classNames from 'classnames';
import { ChangeEvent, FocusEvent, useEffect, useRef } from 'react';
import { Search, XCircle } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { SharedProps } from '../types';
import { Button } from './Button';
import { Icon } from './Icon';
import { Input, InputProps } from './Input';
import { InputWrapper } from './InputWrapper';

interface SearchFilterInputProps {
  /**
   * If a name is given, it will be used to prefix the URL param.
   * Use if multiple search inputs are on the same page.
   */
  name?: string;
  id?: string;
  placeholder?: string;
  autoFocus?: boolean;
  onChange?: (value: string) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  payload?: (value: string) => object;
  className?: string;
  inputSize?: InputProps['inputSize'];
}

export function SearchFilterInput({
  name = 'search',
  id,
  placeholder,
  autoFocus,
  onChange,
  onFocus,
  onBlur,
  payload = () => ({ page: undefined }),
  className,
  inputSize,
}: SearchFilterInputProps) {
  const { t } = useTranslation();
  const { params } = usePage<SharedProps>().props;
  const value = params[name] as string || '';

  const inputRef = useRef<HTMLInputElement | null>(null);
  const timerRef = useRef<number>();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    window.clearTimeout(timerRef.current);

    const delay = import.meta.env.VITEST ? 25 : 300;

    timerRef.current = window.setTimeout(() => {
      const value = event.target.value;

      router.get(
        '',
        {
          [name]: value || undefined,
          ...payload(value),
        },
        { preserveScroll: true, preserveState: true, replace: true },
      );

      onChange?.(value);
    }, delay);
  };

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const handleReset = () => {
    router.get(
      '',
      { [name]: undefined },
      { preserveScroll: true, preserveState: true, replace: true },
    );

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <InputWrapper>
      <label htmlFor={id}>
        <Icon className={classNames(value ? 'text-slate-800' : 'text-slate-400')}>
          <Search />
        </Icon>
      </label>
      <Input
        aria-label={t('shared:filter.search')}
        role="searchbox"
        defaultValue={value || ''}
        onChange={handleChange}
        onFocus={onFocus}
        onBlur={onBlur}
        className={classNames('pl-9', className)}
        id={id}
        autoFocus={autoFocus}
        placeholder={typeof placeholder !== 'undefined' ? placeholder : t('shared:filter.search')}
        inputSize={inputSize}
        ref={inputRef}
      />
      {value && (
        <Button
          onClick={handleReset}
          variant="tertiary"
          className="relative text-slate-400 hover:text-slate-800 !border-transparent"
          aria-label={t('shared:filter.clear')}
          size={inputSize}
        >
          <Icon>
            <XCircle />
          </Icon>
        </Button>
      )}
    </InputWrapper>
  );
}
